import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './TeamSales.css';
import AdminLoading from './AdminLoading';
import { extractCreatorId, getCreatorDisplayName } from '../utils/authUtils';
import { adminUserService } from '../../services/adminUserService';
import { fetchAllAdminOrdersForAgg, ADMIN_LIST_PAGE_LIMIT } from '../utils/adminOrdersBulkFetch';
import {
  buildSalesReport,
  filterOrdersByDate,
  formatSalesMoney,
} from '../utils/teamSalesReport';

function formatMoney(n) {
  return formatSalesMoney(n);
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-PK', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

const DATE_PRESET_OPTIONS = [
  { id: 'all', label: 'All loaded' },
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'custom', label: 'Custom' },
];

const TeamSales = ({ currentUser }) => {
  const isTeamMember = currentUser?.role === 'team';
  const myId = String(currentUser?._id || currentUser?.id || '');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [adminPhase, setAdminPhase] = useState('overview'); // 'overview' | 'member'
  const [selectedMember, setSelectedMember] = useState(null);
  const [datePreset, setDatePreset] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchAllAdminOrdersForAgg();
      setOrders(list);
    } catch (e) {
      console.error('TeamSales orders:', e);
      setError(e.message || 'Could not load sales data');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTeamUsers = useCallback(async () => {
    if (isTeamMember) return;
    try {
      const acc = [];
      for (let page = 1; page <= 200; page += 1) {
        let res = await adminUserService.getUsers({
          role: 'team',
          limit: ADMIN_LIST_PAGE_LIMIT,
          page,
        });
        let list = res?.success && Array.isArray(res.data) ? res.data : [];
        if (list.length === 0 && page === 1) {
          res = await adminUserService.getUsers({ limit: ADMIN_LIST_PAGE_LIMIT, page: 1 });
          const all = res?.success && Array.isArray(res.data) ? res.data : [];
          list = all.filter((u) => String(u?.role || '').toLowerCase() === 'team');
          acc.push(...list);
          break;
        }
        acc.push(...list);
        if (list.length < ADMIN_LIST_PAGE_LIMIT) break;
      }
      setTeamUsers(acc);
    } catch {
      setTeamUsers([]);
    }
  }, [isTeamMember]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchTeamUsers();
  }, [fetchTeamUsers]);

  const filteredOrders = useMemo(
    () => filterOrdersByDate(orders, datePreset, customDateFrom, customDateTo),
    [orders, datePreset, customDateFrom, customDateTo]
  );

  const dateRangeDescription = useMemo(() => {
    if (datePreset === 'all') {
      const n = orders.length;
      return `No date filter · ${n} order${n === 1 ? '' : 's'} loaded from server`;
    }
    if (datePreset === 'today') return 'Orders placed today (local time)';
    if (datePreset === '7d') return 'Last 7 days including today';
    if (datePreset === '30d') return 'Last 30 days including today';
    if (datePreset === 'custom') {
      if (!customDateFrom && !customDateTo) return 'Custom: pick dates or choose another preset';
      const fmt = (s) =>
        s
          ? new Date(`${s}T12:00:00`).toLocaleDateString('en-PK', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : null;
      const a = fmt(customDateFrom);
      const b = fmt(customDateTo);
      if (a && b) return `${a} – ${b}`;
      if (a) return `From ${a}`;
      if (b) return `Through ${b}`;
    }
    return '';
  }, [datePreset, customDateFrom, customDateTo, orders]);

  const teamIdSet = useMemo(() => {
    const s = new Set();
    for (const u of teamUsers) {
      const id = u?._id || u?.id;
      if (id) s.add(String(id));
    }
    return s;
  }, [teamUsers]);

  const teamReport = useMemo(() => {
    if (!myId) return null;
    return buildSalesReport(filteredOrders, (cid) => cid && String(cid) === myId, {
      creatorId: myId,
    });
  }, [filteredOrders, myId]);

  const adminCombinedReport = useMemo(() => {
    if (teamIdSet.size === 0) {
      return buildSalesReport(filteredOrders, () => false);
    }
    let gross = 0;
    let coupon = 0;
    for (const uid of teamIdSet) {
      const rep = buildSalesReport(filteredOrders, (cid) => cid && String(cid) === uid, {
        creatorId: uid,
      });
      gross += rep.grossLines;
      coupon += rep.couponDiscountTotal;
    }
    const base = buildSalesReport(filteredOrders, (cid) => cid && teamIdSet.has(String(cid)));
    return {
      ...base,
      grossLines: gross,
      couponDiscountTotal: coupon,
      netLineRevenue: Math.max(0, gross - coupon),
    };
  }, [filteredOrders, teamIdSet]);

  const memberSummaries = useMemo(() => {
    if (isTeamMember) return [];
    return teamUsers.map((u) => {
      const uid = String(u._id || u.id || '');
      const rep = buildSalesReport(filteredOrders, (cid) => cid && String(cid) === uid, {
        creatorId: uid,
      });
      return {
        user: u,
        ...rep,
      };
    });
  }, [filteredOrders, teamUsers, isTeamMember]);

  const selectedMemberReport = useMemo(() => {
    if (!selectedMember) return null;
    const uid = String(selectedMember._id || selectedMember.id || '');
    return buildSalesReport(filteredOrders, (cid) => cid && String(cid) === uid, {
      creatorId: uid,
    });
  }, [filteredOrders, selectedMember]);

  const renderDateFilterBar = () => (
    <div className="team-sales-date-bar">
      <span className="team-sales-date-bar-label">
        <i className="fas fa-calendar-alt" aria-hidden />
        Order date
      </span>
      <div className="team-sales-date-presets" role="group" aria-label="Date range">
        {DATE_PRESET_OPTIONS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`team-sales-date-preset ${datePreset === p.id ? 'active' : ''}`}
            onClick={() => setDatePreset(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      {datePreset === 'custom' && (
        <div className="team-sales-date-inputs">
          <label className="team-sales-date-field">
            <span>From</span>
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
            />
          </label>
          <label className="team-sales-date-field">
            <span>To</span>
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
            />
          </label>
        </div>
      )}
    </div>
  );

  const renderReportBlocks = (report, { productsNote, paymentsNote, paymentsColLabel, ordersLabel }) => {
    if (!report) return null;
    const ordersLbl = ordersLabel || 'Orders (with these products)';
    const payCol = paymentsColLabel || 'Line total';
    return (
      <>
        <div className="team-sales-grid">
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">Sold items (units)</div>
            <div className="team-sales-stat-value">{report.unitsSold.toLocaleString('en-PK')}</div>
          </div>
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">{ordersLbl}</div>
            <div className="team-sales-stat-value">{report.orderCount.toLocaleString('en-PK')}</div>
          </div>
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">Total payments (line items)</div>
            <div className="team-sales-stat-value">{formatMoney(report.grossLines)}</div>
            <div className="team-sales-stat-sub">Sum of matching line totals (shipping not split)</div>
          </div>
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">COD payments</div>
            <div className="team-sales-stat-value">{formatMoney(report.codLines)}</div>
            <div className="team-sales-stat-sub">From COD orders only</div>
          </div>
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">Non-COD payments</div>
            <div className="team-sales-stat-value">{formatMoney(report.nonCodLines)}</div>
          </div>
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">Coupon discounts (your share)</div>
            <div className="team-sales-stat-value">{formatMoney(report.couponDiscountTotal || 0)}</div>
            <div className="team-sales-stat-sub">From promo codes on your lines / your shop coupons</div>
          </div>
          <div className="team-sales-stat">
            <div className="team-sales-stat-label">Net after coupons</div>
            <div className="team-sales-stat-value">{formatMoney(report.netLineRevenue ?? report.grossLines)}</div>
            <div className="team-sales-stat-sub">Line payments minus attributed coupon discount</div>
          </div>
        </div>

        <div className="team-sales-panel">
          <div className="team-sales-panel-head">
            <h2>Products sold</h2>
            <span className="team-sales-note">{productsNote}</span>
          </div>
          {report.products.length === 0 ? (
            <div className="team-sales-empty">No sold products in loaded orders.</div>
          ) : (
            <div className="team-sales-table-wrap">
              <table className="team-sales-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="num">Qty</th>
                    <th className="num">Revenue</th>
                    <th className="num">COD</th>
                    <th className="num">Other</th>
                  </tr>
                </thead>
                <tbody>
                  {report.products.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td className="num">{row.quantity}</td>
                      <td className="num">{formatMoney(row.revenue)}</td>
                      <td className="num">{formatMoney(row.codRevenue)}</td>
                      <td className="num">{formatMoney(row.nonCodRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="team-sales-panel">
          <div className="team-sales-panel-head">
            <h2>Payments</h2>
            <span className="team-sales-note">{paymentsNote}</span>
          </div>
          {report.payments.length === 0 ? (
            <div className="team-sales-empty">No payments yet.</div>
          ) : (
            <div className="team-sales-table-wrap">
              <table className="team-sales-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Method</th>
                    <th className="num">{payCol}</th>
                    <th className="num">Coupon</th>
                    <th className="num">Order total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.payments.map((row) => (
                    <tr key={String(row.orderId)}>
                      <td>#{row.orderNumber}</td>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>
                        {row.paymentMethod}
                        {row.cod && (
                          <>
                            {' '}
                            <span className="team-sales-cod-pill">COD</span>
                          </>
                        )}
                      </td>
                      <td className="num">{formatMoney(row.myShare)}</td>
                      <td className="num">
                        {(row.couponShare || 0) > 0 ? (
                          <>
                            -{formatMoney(row.couponShare)}
                            {row.couponCode ? ` (${row.couponCode})` : ''}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="num">{formatMoney(row.orderTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="team-sales-page">
        <AdminLoading message="Loading sales…" />
      </div>
    );
  }

  if (isTeamMember) {
    return (
      <div className="team-sales-page">
        <div className="team-sales-hero">
          <h1>My sales</h1>
          <p>Products you created, payments collected on those lines, and COD breakdown.</p>
        </div>
        {error && <div className="team-sales-error">{error}</div>}
        {renderDateFilterBar()}
        <p className="team-sales-note" style={{ marginBottom: '1rem' }}>
          {dateRangeDescription}. Only <strong>confirmed</strong> and later statuses count as sold;
          pending is excluded; cancelled (including after confirm) is excluded. {orders.length} orders loaded from server.
        </p>
        {renderReportBlocks(teamReport, {
          productsNote: 'Revenue from order line items for products you created.',
          paymentsNote: 'Your product lines per order.',
          paymentsColLabel: 'Your lines',
          ordersLabel: 'Orders (with your products)',
        })}
      </div>
    );
  }

  /* ——— Admin ——— */
  if (adminPhase === 'member' && selectedMember && selectedMemberReport) {
    const name =
      selectedMember.name ||
      selectedMember.email ||
      getCreatorDisplayName(selectedMember) ||
      'Team member';
    return (
      <div className="team-sales-page">
        <button
          type="button"
          className="team-sales-back"
          onClick={() => {
            setAdminPhase('overview');
            setSelectedMember(null);
          }}
        >
          <i className="fas fa-arrow-left" aria-hidden />
          All team sales
        </button>
        <div className="team-sales-hero">
          <h1>Sales — {name}</h1>
          <p>Same detail as the team member sees for their own products.</p>
        </div>
        {error && <div className="team-sales-error">{error}</div>}
        {renderDateFilterBar()}
        <p className="team-sales-note" style={{ marginBottom: '1rem' }}>
          {dateRangeDescription}. Only confirmed+ count as sold; pending excluded; cancelled excluded.{' '}
          {orders.length} orders loaded from server.
        </p>
        {renderReportBlocks(selectedMemberReport, {
          productsNote: 'Line-level totals for this member’s products only.',
          paymentsNote: 'This member’s product lines per order.',
          paymentsColLabel: 'Member lines',
          ordersLabel: 'Orders (their products)',
        })}
      </div>
    );
  }

  return (
    <div className="team-sales-page">
      <div className="team-sales-hero">
        <h1>Team sales</h1>
        <p>
          Aggregate performance for all team members’ products, then open any member for the same
          breakdown they see.
        </p>
      </div>
      {error && <div className="team-sales-error">{error}</div>}
      {renderDateFilterBar()}
      <p className="team-sales-note" style={{ marginBottom: '1rem' }}>
        {dateRangeDescription}. Only confirmed+ count as sold; pending excluded; cancelled excluded.{' '}
        {orders.length} orders loaded from server. Only lines where <strong>product creator</strong> is role{' '}
        <strong>team</strong>.
      </p>

      <div className="team-sales-grid">
        <div className="team-sales-stat">
          <div className="team-sales-stat-label">All team · Units</div>
          <div className="team-sales-stat-value">{adminCombinedReport.unitsSold.toLocaleString('en-PK')}</div>
        </div>
        <div className="team-sales-stat">
          <div className="team-sales-stat-label">All team · Orders</div>
          <div className="team-sales-stat-value">{adminCombinedReport.orderCount.toLocaleString('en-PK')}</div>
        </div>
        <div className="team-sales-stat">
          <div className="team-sales-stat-label">All team · Line payments</div>
          <div className="team-sales-stat-value">{formatMoney(adminCombinedReport.grossLines)}</div>
        </div>
        <div className="team-sales-stat">
          <div className="team-sales-stat-label">All team · COD</div>
          <div className="team-sales-stat-value">{formatMoney(adminCombinedReport.codLines)}</div>
        </div>
        <div className="team-sales-stat">
          <div className="team-sales-stat-label">All team · Coupon discounts</div>
          <div className="team-sales-stat-value">
            {formatMoney(adminCombinedReport.couponDiscountTotal || 0)}
          </div>
        </div>
      </div>

      <div className="team-sales-panel">
        <div className="team-sales-panel-head">
          <h2>Team members</h2>
          <span className="team-sales-note">Click a row for product &amp; payment detail</span>
        </div>
        {memberSummaries.length === 0 ? (
          <div className="team-sales-empty">
            No team users loaded. If the list is empty, check{' '}
            <code>/api/admin/users?role=team</code>.
          </div>
        ) : (
          <div className="team-sales-table-wrap">
            <table className="team-sales-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th className="num">Orders</th>
                  <th className="num">Line payments</th>
                  <th className="num">COD</th>
                  <th className="num">Non-COD</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {memberSummaries.map((row) => {
                  const u = row.user;
                  const label = u.name || u.email || 'Team member';
                  return (
                    <tr key={String(u._id || u.id)}>
                      <td>{label}</td>
                      <td className="num">{row.orderCount}</td>
                      <td className="num">{formatMoney(row.grossLines)}</td>
                      <td className="num">{formatMoney(row.codLines)}</td>
                      <td className="num">{formatMoney(row.nonCodLines)}</td>
                      <td>
                        <button
                          type="button"
                          className="team-sales-linkish"
                          onClick={() => {
                            setSelectedMember(u);
                            setAdminPhase('member');
                          }}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="team-sales-panel">
        <div className="team-sales-panel-head">
          <h2>All team · Products sold (combined)</h2>
        </div>
        {adminCombinedReport.products.length === 0 ? (
          <div className="team-sales-empty">No team-attributed lines in loaded orders.</div>
        ) : (
          <div className="team-sales-table-wrap">
            <table className="team-sales-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="num">Qty</th>
                  <th className="num">Revenue</th>
                  <th className="num">COD</th>
                  <th className="num">Other</th>
                </tr>
              </thead>
              <tbody>
                {adminCombinedReport.products.map((row) => (
                  <tr key={row.key}>
                    <td>{row.name}</td>
                    <td className="num">{row.quantity}</td>
                    <td className="num">{formatMoney(row.revenue)}</td>
                    <td className="num">{formatMoney(row.codRevenue)}</td>
                    <td className="num">{formatMoney(row.nonCodRevenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="team-sales-panel">
        <div className="team-sales-panel-head">
          <h2>All team · Payments (combined)</h2>
        </div>
        {adminCombinedReport.payments.length === 0 ? (
          <div className="team-sales-empty">No payments.</div>
        ) : (
          <div className="team-sales-table-wrap">
            <table className="team-sales-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Method</th>
                  <th className="num">Team lines</th>
                  <th className="num">Order total</th>
                </tr>
              </thead>
              <tbody>
                {adminCombinedReport.payments.map((row) => (
                  <tr key={String(row.orderId)}>
                    <td>#{row.orderNumber}</td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      {row.paymentMethod}
                      {row.cod && (
                        <>
                          {' '}
                          <span className="team-sales-cod-pill">COD</span>
                        </>
                      )}
                    </td>
                    <td className="num">{formatMoney(row.myShare)}</td>
                    <td className="num">{formatMoney(row.orderTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSales;
